import React, { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { FormattedMessage, injectIntl, InjectedIntlProps } from 'react-intl';
import styled, { css } from 'styled-components';
import { CSSTransition } from 'react-transition-group';
import ICONS from '../constants/icons';
import { COLORS, EXTENDS, MQS } from '../constants/styles';

import { TranslationHelpers } from '../../../i18n/translationHelpers';
import Icon from './Icon';
import Tile from './Tile';
import { createFocusTrap } from 'focus-trap';
const Wrapper = styled.div`
    .fade-enter {
        opacity: 0.01;
    }

    .fade-enter.fade-enter-active {
        opacity: 1;
        transition: opacity 100ms ease-in-out;
    }

    .fade-exit {
        opacity: 1;
    }

    .fade-exit.fade-exit-active {
        opacity: 0.01;
        transition: opacity 100ms ease-in-out;
    }

    .initial-focus {
        outline: none;
    }
`;

const Overlay = styled.div`
    ${EXTENDS.overlay};
    align-items: flex-start;
    justify-content: center;
    overflow-x: hidden;
    overflow-y: auto;
    width: 100%;
`;

interface IOverlapContentWrapperProps {
    width?: string;
    isLarge?: boolean;
}

const ContentWrapper = styled.div<IOverlapContentWrapperProps>`
    max-height: 100%;
    max-width: 100%;
    width: ${(props: { width?: string }) => (props.width ? props.width : '450px')};
    margin-top: 9vh;

    @media (max-width: ${MQS.mq2Min}) {
        width: 100vw;
        margin-top: 0;
    }

    @media (min-width: ${MQS.mq2Min}) and (max-width: ${MQS.mq3Min}) {
        width: ${(props) => (props.isLarge ? '500px' : '450px')};
    }

    @media (min-width: ${MQS.mq3Min}) {
        width: ${(props) => (props.isLarge ? '750px' : '450px')};
    }
`;

const modalStyles = css`
    height: auto;
    padding: 50px;

    @media (max-width: ${MQS.mq1Max}) {
        border-radius: 0;
        margin: auto;
        min-height: 100vh;
        padding: 30px;
        width: 100vw;
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
`;

const Title = styled.h2`
    font-size: 1.25rem;
    margin: 0;
    font-weight: normal;

    @media (max-width: ${MQS.mq1Min}) {
        font-size: 0.875rem;
    }

    @media (max-width: ${MQS.mq2Min}) {
        font-size: 1rem;
    }

    @media (max-width: ${MQS.mq3Min}) {
        font-size: 1.125rem;
    }

    @media (max-width: ${MQS.mq4Min}) {
        font-size: 1.25rem;
    }
`;

const Close = styled.span`
    color: ${COLORS.grey};
    cursor: pointer;

    &:hover {
        color: ${COLORS.dark};
    }
`;
type UseFocusTrapOptions = {
    ref: RefObject<HTMLDivElement>;
    isOpen: boolean | undefined;
    onClose: () => void;
};
function useFocusTrap({ ref, isOpen, onClose }: UseFocusTrapOptions): void {
    useEffect(() => {
        if (!isOpen || !ref.current) {
            return;
        }

        const trap = createFocusTrap(ref.current, {
            clickOutsideDeactivates: false,
            escapeDeactivates: true,
            returnFocusOnDeactivate: true,
            onDeactivate: onClose,
            allowOutsideClick: true,
        });
        trap.activate();

        return () => {
            trap.deactivate({ onDeactivate: () => {} });
        };
    }, [ref, isOpen, onClose]);
}
interface IOverlayDialogProps {
    isOpen?: boolean;
    title?: FormattedMessage.MessageDescriptor;
    username?: string;
    children: ReactNode;
    onClose: () => void;
    closeOnOutside?: boolean;
    contentWidth?: string;
    isLarge?: boolean;
}

const OverlayDialog: React.FC<IOverlayDialogProps & InjectedIntlProps> = ({
    contentWidth,
    isOpen,
    isLarge,
    title,
    children,
    onClose,
    username,
    closeOnOutside = false,
    intl,
}) => {
    const [scrollX, setScrollX] = useState(window.scrollX);
    const [scrollY, setScrollY] = useState(window.scrollY);
    const ref = useRef<HTMLDivElement>(null);
    const closeRef = useRef<HTMLSpanElement | null>(null);

    useEffect(() => {
        if (isOpen) {
            disableBackgroundScroll();
            if (closeRef.current) {
                const closeEL = closeRef.current;
                closeEL.classList.add('initial-focus');
            }
        } else {
            enableBackgroundScroll();
        }
        return enableBackgroundScroll;
    }, [isOpen]);

    const enableBackgroundScroll = () => {
        const htmlEl = document.querySelector('html');
        if (htmlEl) {
            htmlEl.classList.remove('scroll-lock');
            htmlEl.style.top = '';
            window.scrollTo(scrollX, scrollY);
        }
    };

    const disableBackgroundScroll = () => {
        const currentScrollY = window.scrollY;
        setScrollX(window.scrollX);
        setScrollY(currentScrollY);
        const htmlEl = document.querySelector('html');
        if (htmlEl) {
            htmlEl.style.top = `-${currentScrollY}px`;
            htmlEl.classList.add('scroll-lock');
        }
    };

    const clickOutside = (e: React.SyntheticEvent) => {
        if (closeOnOutside && e.currentTarget === e.target) {
            onClose();
        }
    };

    const getTitle = (
        title: FormattedMessage.MessageDescriptor | undefined,
        username: string | undefined,
    ) => {
        if (title && username) {
            return (
                <Title>
                    {TranslationHelpers.getFormattedMessage(title.id, {
                        username,
                    })}
                </Title>
            );
        } else if (title) {
            return (
                <Title>
                    {TranslationHelpers.getFormattedMessage(title.id, (title as any).values)}
                </Title>
            );
        }
    };
    useFocusTrap({ ref, isOpen, onClose });
    if (!isOpen) {
        return null;
    }
    const closeAriaLabel = intl.formatMessage(
        TranslationHelpers.getMessageDescriptor('close.ariaLabel'),
    );

    return (
        <Wrapper className="overlay-wrapper">
            <CSSTransition
                in={isOpen}
                timeout={100}
                classNames="fade"
                mountOnEnter={true}
                unmountOnExit={true}
            >
                <Overlay onClick={clickOutside}>
                    <ContentWrapper width={contentWidth} isLarge={isLarge}>
                        <div role="dialog" aria-modal="true" ref={ref}>
                            <Tile localStyles={modalStyles}>
                                <Header>
                                    {getTitle(title, username)}
                                    <Close
                                        onClick={onClose}
                                        data-qa-id="close-overlay"
                                        tabIndex={0}
                                        innerRef={(el: HTMLSpanElement | null) => {
                                            closeRef.current = el;
                                        }}
                                        onBlur={() => {
                                            if (closeRef.current) {
                                                closeRef.current.classList.remove('initial-focus');
                                            }
                                        }}
                                    >
                                        <Icon
                                            icon={ICONS.CLOSE}
                                            size="18"
                                            ariaLabel={closeAriaLabel}
                                        />
                                    </Close>
                                </Header>
                                {children}
                            </Tile>
                        </div>
                    </ContentWrapper>
                </Overlay>
            </CSSTransition>
        </Wrapper>
    );
};

export default injectIntl(OverlayDialog);
